<script setup lang="ts">
  import { computed } from 'vue';

  import type { Attachment } from '@stuut/api/models';
  import { Dialog, DialogContent, DialogTrigger } from '@ui/components/dialog';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import AttachmentPreview from '@/components/attachments/AttachmentPreview.vue';

  interface Props {
    attachment: Attachment;
    failed?: boolean;
    finalized?: boolean;
    truncate?: number;
    uploading?: boolean;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'delete'): void;
  }>();

  const isOpen = defineModel<boolean>('open', { default: false });

  const isTruncated = computed(
    () => props.truncate && props.attachment?.name?.length > props.truncate,
  );

  const displayFilename = computed(() => {
    if (isTruncated.value) {
      return props.attachment?.name?.slice(0, props.truncate);
    }

    return props.attachment?.name;
  });
</script>

<template>
  <div
    class="flex flex-row w-full text-xs max-w-fit px-2 py-1 border-2 gap-x-3 items-center rounded bg-muted/30"
  >
    <Dialog v-model:open="isOpen">
      <Tooltip :disabled="!isTruncated">
        <TooltipTrigger as-child>
          <DialogTrigger as-child>
            <div :class="{ 'pointer-events-none': uploading }">
              <slot name="trigger">
                <span
                  class="font-medium text-2xs space-x-1"
                  :class="
                    failed
                      ? 'text-destructive'
                      : props.attachment.src
                        ? 'text-blue hover:text-blue/50 cursor-pointer'
                        : 'text-foreground'
                  "
                  @click.prevent
                >
                  <i class="fa-solid fa-paperclip text-agent"></i>
                  <span> {{ displayFilename }}<span v-if="isTruncated">...</span> </span>
                </span>
              </slot>
            </div>
          </DialogTrigger>
        </TooltipTrigger>

        <TooltipContent>
          {{ props.attachment?.name }}
        </TooltipContent>
      </Tooltip>
      <DialogContent class="overflow-scroll min-h-[80vh] max-h-[90vh] p-3">
        <AttachmentPreview :attachment="attachment" />
      </DialogContent>
    </Dialog>

    <i v-if="uploading" class="fa fa-spinner-third animate-spin" />
    <i
      v-else-if="!finalized"
      class="fa fa-times cursor-pointer hover:text-foreground/50"
      @click="emits('delete')"
    />
  </div>
</template>

<style scoped></style>
